/**
 * FORGE COGNITIVE OS — Runtime Configuration Layer
 * ══════════════════════════════════════════════════════════════════════════
 * SELF-HOSTING CORE DIRECTIVE: unica fonte di verità per tutti gli URL
 * runtime. Nessun altro modulo deve costruire URL autonomamente.
 *
 * Filosofia:
 *   - HTTP API   → same-origin ("/api/...") oppure window.location.origin
 *   - WebSocket  → derivato da window.location (ws:// o wss://)
 *   - Zero dipendenza da "" (eliminata)
 *   - Zero hardcoding (nessun dominio, nessun preview, nessun localhost)
 *
 * Il valore "" è VOLUTAMENTE ignorato:
 * anche se qualcuno lo popola in .env, questo layer non lo legge.
 * ══════════════════════════════════════════════════════════════════════════
 */

// Legge il runtime injected config se presente (index.html può iniettare
// window.FORGE_RUNTIME per casi speciali cross-origin, es. dev proxy).
const runtimeInjected =
  typeof window !== "undefined" && window.FORGE_RUNTIME
    ? window.FORGE_RUNTIME
    : {};

/**
 * Origin dell'app corrente. In SSR è vuoto → fallback stringa vuota.
 */
export const APP_ORIGIN =
  typeof window !== "undefined" && window.location
    ? window.location.origin
    : "";

/**
 * Backend HTTP base URL. Same-origin di default.
 * Il backend è raggiungibile via nginx reverse-proxy su /api/*.
 */
export const BACKEND_URL = runtimeInjected.backend_url || "";

/**
 * WebSocket base URL. Derivato da window.location per rispettare
 * http↔ws e https↔wss automaticamente.
 */
export const WS_URL = (() => {
  if (runtimeInjected.ws_url) return runtimeInjected.ws_url;
  if (typeof window === "undefined" || !window.location) return "";
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}`;
})();

/**
 * API base path per fetch/axios. Same-origin → "/api".
 * Non concatenare mai a mano l'origin: usa questo.
 */
export const API_BASE = `${BACKEND_URL}/api`;

/**
 * Helper: costruisce una URL API assoluta (rispetto all'origin del browser).
 * Uso: apiUrl("/kernel/aria/state") → "/api/kernel/aria/state"
 *
 * Accetta path con o senza slash iniziale.
 */
export function apiUrl(path) {
  const p = String(path || "");
  if (p.startsWith("/api/")) return `${BACKEND_URL}${p}`;
  if (p.startsWith("api/")) return `${BACKEND_URL}/${p}`;
  const norm = p.startsWith("/") ? p : `/${p}`;
  return `${API_BASE}${norm}`;
}

/**
 * Helper: costruisce una URL WebSocket assoluta.
 * Uso: wsUrl("/api/kernel/aria/stream") → "ws://host/api/kernel/aria/stream"
 */
export function wsUrl(path) {
  const p = String(path || "");
  const norm = p.startsWith("/") ? p : `/${p}`;
  return `${WS_URL}${norm}`;
}

/**
 * Diagnostics: espone lo stato del config layer.
 * Utile per debug e per la Self-Hosting Certification.
 */
export const RUNTIME_INFO = {
  layer_version: "1.0.0",
  mode: "same-origin",
  app_origin: APP_ORIGIN,
  backend_url: BACKEND_URL || "(same-origin)",
  ws_url: WS_URL || "(same-origin)",
  api_base: API_BASE,
  injected: Object.keys(runtimeInjected).length > 0,
};

export default {
  APP_ORIGIN,
  BACKEND_URL,
  WS_URL,
  API_BASE,
  apiUrl,
  wsUrl,
  RUNTIME_INFO,
};
